import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface MegaPeekCardProps {
  /** Destination route, already language-prefixed by the caller. */
  to: string
  /** Image src (absolute, served from /public). */
  src: string
  onItemClick: () => void
  /** Caption markup rendered below the image (display text, address rows, etc.). */
  children: React.ReactNode
}

/**
 * Featured "peek inside" card used across the mega menus (Services, Trichology,
 * Salons). On hover:
 *   - the image zooms (scale-105, 700ms)
 *   - a mocha veil fades over the photo
 *   - a centered "Læs mere" label appears with a short gold hairline wiping in
 *     left→right beneath it (a one-beat delay so it reads as deliberate)
 *
 * The caption is supplied by the caller via `children`, so each menu keeps its
 * own caption shape while the hover vocabulary lives here in one place. All
 * overlay layers are aria-hidden — the real caption is the accessible label.
 *
 * Motion stays within the design system: canonical easing
 * cubic-bezier(0.22,1,0.36,1), 500ms hover / 700ms image-zoom durations.
 */
export default function MegaPeekCard({ to, src, onItemClick, children }: MegaPeekCardProps) {
  const { t } = useTranslation()
  return (
    <Link to={to} onClick={onItemClick} className="group block">
      <div className="relative aspect-[4/3] overflow-hidden bg-mocha/5">
        <img
          src={src}
          alt=""
          loading="eager"
          fetchPriority="low"
          className="w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
        />
        {/* uniform veil */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-mocha/35 opacity-0 transition-opacity duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:opacity-100"
        />
        {/* centered label + hairline wipe */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1.5 opacity-0 transition-opacity duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:opacity-100"
        >
          <span className="font-body text-[11px] tracking-[0.22em] uppercase text-ivory">
            {t('megaServices.readMore')}
          </span>
          <span className="block h-px w-10 origin-left scale-x-0 bg-gold transition-transform duration-500 delay-100 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100" />
        </span>
      </div>
      {children}
    </Link>
  )
}
